// Session, role and permission helpers. Previously scattered across
// App.js, Dashboard.jsx and every module's inline sessionStorage reads.

import type { AuthUser, PermissionMap } from '../types/admin';

export const STORAGE_KEYS = {
  user: 'church_erp_user',
  token: 'church_erp_token',
  lang: 'church_erp_lang',
  role: 'church_erp_role',
  moduleAccess: 'church_erp_module_access'
} as const;

export const getStoredUser = (): AuthUser | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.user);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
};

export const storeSession = (user: AuthUser, langCode: string, token: string): void => {
  sessionStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
  sessionStorage.setItem(STORAGE_KEYS.token, token);
  if (langCode) sessionStorage.setItem(STORAGE_KEYS.lang, langCode);
  if (user?.userRole || user?.role) {
    sessionStorage.setItem(STORAGE_KEYS.role, (user.userRole || user.role) as string);
  }
};

export const clearSession = (): void => sessionStorage.clear();

export const getStoredLang = (): string => sessionStorage.getItem(STORAGE_KEYS.lang) || 'en';

/** Compared in lower case, as the server does (Roles.java): "ADMIN" is an administrator too. */
const ADMIN_ROLES = ['system administrator', 'admin'];

/**
 * A Sunday School teacher's login. The role is what the server keys its
 * section-level rule on, so the screens use the same name to show that user
 * only their own sections.
 */
export const TEACHER_ROLE = 'Sunday School Teacher';
export const isTeacherLogin = (user?: AuthUser | null): boolean =>
  (user?.userRole || user?.role) === TEACHER_ROLE;

export const isAdmin = (user?: AuthUser | null): boolean =>
  ADMIN_ROLES.includes(String(user?.userRole || user?.role || '').trim().toLowerCase());

/** Permissions arrive from the backend as a JSON string on user.permissions. */
export const parsePermissions = (user?: AuthUser | null): PermissionMap => {
  if (user?.parsedPermissions) return user.parsedPermissions;
  try {
    return user?.permissions ? (JSON.parse(user.permissions) as PermissionMap) : {};
  } catch {
    return {};
  }
};

/**
 * Modules that absorbed older ones keep honouring the old permission keys, so a
 * user's saved permission blob need not be migrated when modules merge.
 */
const LEGACY_MODULE_IDS: Record<string, string[]> = {
  'Members & Families': ['Member Management', 'Family Details'],
  Offerings: ['Donations', 'Collection/Offering'],
  Baptism: ['Child Baptism', 'Baptism Confirmation'],
  Marriage: ['Marriage Banns', 'Marriage Certificate']
};

/** A module's own key plus any legacy keys it replaced. */
const permissionKeysFor = (moduleId: string): string[] =>
  [moduleId, ...(LEGACY_MODULE_IDS[moduleId] || [])];

export const hasModuleAccess = (user: AuthUser | null | undefined, moduleId: string): boolean => {
  if (isAdmin(user)) return true;
  const perms = parsePermissions(user) as Record<string, string | undefined>;
  return permissionKeysFor(moduleId).some((key) => Boolean(perms[key]) && perms[key] !== 'None');
};

export const isReadOnly = (user: AuthUser | null | undefined, moduleId: string): boolean => {
  if (isAdmin(user)) return false;
  const perms = parsePermissions(user) as Record<string, string | undefined>;
  // Read-only only if every key that grants access grants it read-only; a single
  // full-access key (new or legacy) means full access.
  const granted = permissionKeysFor(moduleId)
    .map((key) => String(perms[key] || '').toLowerCase())
    .filter((level) => level && level !== 'none');
  return granted.length > 0 && granted.every((level) => level.includes('read'));
};

/** Global read-only lock — mirrors the body-class logic in App.js. */
export const isGlobalReadOnly = (user?: AuthUser | null): boolean => {
  const role = user?.role || user?.userRole || sessionStorage.getItem(STORAGE_KEYS.role) || '';
  const access = user?.accessLevel || sessionStorage.getItem(STORAGE_KEYS.moduleAccess) || '';
  return String(role).toLowerCase().includes('read') || String(access).toLowerCase().includes('read');
};
